import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";

import { AlertProps, SetModalProps, UIContextProps } from "./types";
import { User } from "../../models/UserInterface";
import { useAuth } from "../AuthContext";

const UIContext = createContext<UIContextProps | undefined>(undefined);

const defaultUser: User = {
  name: "",
  email: "",
  permissions: {},
};

type UIProviderProps = {
  children: ReactNode;
};

export const UIProvider = ({ children }: UIProviderProps) => {
  const { userData } = useAuth();
  const [modalProps, setModalProps] = useState<SetModalProps | null>(null);
  const [alertProps, setAlertProps] = useState<AlertProps | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [appLoading, setAppLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  const getUserId = (): string | null => {
    return user?.email ? user.email.split("@")[0].toLowerCase() : "unknown";
  };

  /*********************************
   * Toggle Sidebar open/close state
   */
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  /************
   * Close Modal
   */
  const closeModal = () => {
    setModalProps(null);
  };

  /************
   * Close Alert
   */
  const closeAlert = () => {
    setAlertProps(null);
  };

  /*********************
   * Root page component
   */
  const mainBody = useMemo(() => {
    return document.querySelector("#rootPage");
  }, [modalProps, appLoading]);

  // Modal Background Blur effect
  useEffect(() => {
    if (!mainBody || (mainBody && !(mainBody instanceof HTMLElement))) return;
    mainBody.style.filter = appLoading || modalProps ? "blur(5px)" : "blur(0)";
  }, [mainBody, modalProps, appLoading]);

  useEffect(() => {
    if (userData) {
      setUser({ ...defaultUser, ...userData });
    }
  }, [userData]);

  return (
    <UIContext.Provider
      value={{
        setAppModal: setModalProps,
        setAlert: setAlertProps,
        setSidebarOpen,
        setAppLoading,
        toggleSidebar,
        closeAlert,
        closeModal,
        getUserId,
        setUser,
        sidebarOpen,
        appLoading,
        modalProps,
        alertProps,
        user,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useAppUI = (): UIContextProps => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error("useAppUI must be used within an AppProvider");
  }
  return context;
};
